<template>
    <div class="timeline-messages">
        <template v-for="(group, groupIndex) in groups">
            <group-header
                :hasYearMarker="groupIndex !== 0 && group.hasYearMarker"
                :date="group.date"
                :count="group.messages.length"
                :key="`header-${groupIndex}`"/>
            <message-item
                v-for="(message, messageIndex) in group.messages"
                :key="`${groupIndex}-${messageIndex}`"
                :has-details="message.hasDetails"
                :message-id="message.id"
                :anchor-id="`#message.anchorId`"
                :is-highlighted="isHighlightedMessage(message)"
                :class="messageItemClasses(message)"
                @mouseover="() => mouseOverItem(message)"
                @mouseleave="mouseLeaveItem">

                <component
                    :type="type"
                    :entity-id="entityId"
                    :message="message"
                    @delete="deleteMessage(message)"
                    @download-attachment="downloadAttachment"
                    :is="getMessageComponent(message)"
                />
            </message-item>
        </template>
    </div>
</template>

<script>
    import Vue from 'vue';
    import Shortcuts from '@/common/shortcuts/shortcuts';
    import TimelineMessageModel from '@/common/models/timeline-message-model';
    import MessageItem from '@/components/timeline/components/message-item';
    import GroupHeader from './group-header';
    import getMessageComponentByType from './get-message-component-by-type';
    import {shortcutTypes} from '@/components/timeline/components/shortcut-types';

    export default {
        name: 'message-list',
        components: {MessageItem, GroupHeader},
        props: {
            type: {
                type: String,
                required: true,
            },
            entityId: {
                type: String,
                required: true,
            },
            groups: {
                type: Array,
                required: true,
            },
            expanded: {
                type: Boolean,
            },
        },
        data() {
            return {
                locationHash: document.location.hash,
                hoveredMessageModel: null,
                shortcutBus: new Vue(),
            };
        },
        provide() {
            return {
                shortcutBus: this.shortcutBus,
            };
        },
        mounted() {
            document.body.addEventListener('click', this.onBodyClick);
            window.addEventListener('hashchange', this.onHashChange);
            if (this.expanded) {
                this.shortcutBus.$emit(shortcutTypes.expandAllEvents);
            }
        },
        created() {
            const isBodyFocused = () => !document.activeElement || document.activeElement === document.body;
            this.shortcuts = [
                Shortcuts.register('e', () => {
                    if (this.hoveredMessageModel) {
                        this.shortcutBus.$emit(shortcutTypes.expandEvent, this.hoveredMessageModel.id);
                    } else if (isBodyFocused()) {
                        this.shortcutBus.$emit(shortcutTypes.expandAllEvents);
                    }
                }),
                Shortcuts.register('c', () => {
                    if (this.hoveredMessageModel) {
                        this.shortcutBus.$emit(shortcutTypes.closeEvent, this.hoveredMessageModel.id);
                    } else if (isBodyFocused()) {
                        this.shortcutBus.$emit(shortcutTypes.closeAllEvents);
                    }
                }),
                Shortcuts.shift.up.register(() => this.navigateToUpMessage()),
                Shortcuts.shift.down.register(() => this.navigateToDownMessage()),
            ];
        },
        beforeDestroy() {
            this.shortcuts.forEach(shortcut => shortcut.unregister());
            window.removeEventListener('hashchange', this.onHashChange);
            document.body.removeEventListener('click', this.onBodyClick);
        },
        methods: {
            mouseOverItem(message) {
                this.hoveredMessageModel = message;
            },
            mouseLeaveItem() {
                this.hoveredMessageModel = null;
            },
            findMessage(condition) {
                const groups = this.groups.filter(({messages}) => !!messages.length);
                const allMessages = groups.reduce((memo, {messages}) => ([...memo, ...messages]), []);

                for (let i = 0; i < allMessages.length; i += 1) {
                    const index = allMessages.findIndex(message => condition(message));
                    if (index >= 0) {
                        return {
                            prev: allMessages[index - 1] || null,
                            next: allMessages[index + 1] || null,
                            active: allMessages[index],
                        };
                    }
                }
                return null;
            },
            findNextMessageAnchorId(dir) {
                const findMessage = (condition) => {
                    const params = this.findMessage(condition);
                    if (params) {
                        const next = dir > 0 ? params.next : params.prev;
                        return {next, active: params.active};
                    }
                    return null;
                };
                // return next message if some message is hovered
                if (this.hoveredMessageModel) {
                    const params = findMessage(msg => msg.id === this.hoveredMessageModel.id);
                    if (params) {
                        const {next} = params;
                        return next
                            ? {active: this.hoveredMessageModel, next}
                            : {active: this.hoveredMessageModel};
                    }
                }
                // return next message if some message highlighted
                if (this.locationHash) {
                    const params = findMessage(msg => this.locationHash.includes(msg.anchorId));
                    if (params) {
                        const {next, active} = params;
                        return {active, next};
                    }
                }
                // return next message by default first or last
                if (this.groups.length) {
                    const groupsWithMessages = this.groups.filter(group => !!group.messages.length);
                    const group = dir < 0 ? groupsWithMessages[groupsWithMessages.length - 1] : groupsWithMessages[0];
                    // return empty object if can't found group
                    if (!group) {
                        return {};
                    }
                    const message = dir < 0 ? group.messages[group.messages.length - 1] : group.messages[0];
                    return {
                        next: message,
                    };
                }
                return {};
            },
            navigateToMessage({active, next}) {
                if (active) {
                    this.shortcutBus.$emit(shortcutTypes.closeEvent, active.id);
                }
                if (next) {
                    document.location.hash = next.anchorId;
                    this.hoveredMessageModel = next;
                    setTimeout(() => {
                        // when hash is changed
                        // scroll can not get in time,
                        // so, please wait some time and then scroll
                        this.shortcutBus.$emit(shortcutTypes.scrollToEvent, this.hoveredMessageModel.id);
                    }, 100);
                }
            },
            navigateToUpMessage() {
                return this.navigateToMessage(this.findNextMessageAnchorId(-1));
            },
            navigateToDownMessage() {
                return this.navigateToMessage(this.findNextMessageAnchorId(1));
            },
            deleteMessage(message) {
                this.$emit('delete-message', message);
            },
            downloadAttachment(attachment) {
                this.$emit('download-attachment', attachment);
            },
            getMessageComponent(message) {
                return getMessageComponentByType(message.type);
            },
            isHighlightedMessage(message) {
                return this.locationHash.includes(message.anchorId);
            },
            messageItemClasses(message) {
                if ([
                    TimelineMessageModel.Types.gatewayAccountCommentCreated,
                    TimelineMessageModel.Types.timelineCommentCreated,
                    TimelineMessageModel.Types.customerCommentCreated,
                ].includes(message.type)) {
                    return 'timeline-comment-message';
                }
                return 'timeline-message';
            },
            clearDocumentHash() {
                if (history.pushState) {
                    history.pushState('', null, window.location.pathname);
                } else {
                    document.location.hash = '';
                }
                this.locationHash = '';
                this.hoveredMessageModel = null;
            },
            onBodyClick(evt) {
                if (!evt.target.closest('.timeline-message-highlight')) {
                    this.clearDocumentHash();
                }
            },
            onHashChange() {
                this.locationHash = document.location.hash;
            },

        },
    };
</script>
